/**
 * Who is signed in on this device, from the session token itself.
 *
 * Every sign-in path stores an access token (a JWT whose `sub` is the user
 * id); not every path stores `@user_id` (the email-verification link signs
 * in with the token alone). So the token is the one reliable answer. This
 * is not a validity check — the API does that — only "whose session is
 * this", used to bind device-held secrets to their account.
 */
import { getAuthToken } from './tokenStorage';

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  if (typeof globalThis.atob === 'function') {
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return globalThis.atob(padded);
  }
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const ch of b64) {
    const v = B64.indexOf(ch);
    if (v < 0) continue;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >> bits) & 0xff);
    }
  }
  return out;
}

/** The `sub` of a JWT, or null when the token is not a readable JWT. */
export function userIdFromToken(token: string | null | undefined): string | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return typeof payload?.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
}

/** The signed-in user's id, or null when nobody is signed in. */
export async function getSignedInUserIdFromSession(): Promise<string | null> {
  return userIdFromToken(await getAuthToken());
}
